import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { useT } from "../lib/LanguageContext";

/**
 * Copy-to-clipboard for an identifier the operator has to paste elsewhere.
 *
 * Shared by every super-admin ledger (`TransactionsList`, `ClientTransactionsList`): the
 * refund modal (`Tenant360`, `data-testid="modal-tx-id"`) is a blind free-text field —
 * the operator must already know the exact id string, and these tables are the only
 * places that id is visible. It has to be transferable without retyping.
 *
 * `width` narrows the VISIBLE truncation without touching what is copied. A 36-character
 * UUID is the widest thing on any of these tables and the least often read — on the
 * gateway payments console it alone took 306 px of a 958 px container, pushing the
 * practice, the reason and the item off screen behind a horizontal scrollbar. The prefix
 * is enough to recognise a row; the clipboard still carries the whole id.
 *
 * The Copy icon is visible at rest (a hover-only affordance is invisible on touch and to
 * anyone who has not already learnt it is there). The button is named by `aria-label`
 * (the visible text is a truncated id, which says nothing about what pressing does) and a
 * polite live region announces a successful copy, because the icon swap alone is silent.
 */
export default function IdCell({ value, width = "max-w-[16rem]" }) {
  const t = useT("common");
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-slate-300">—</span>;
  return (
    <>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          } catch {
            /* clipboard unavailable (insecure origin) — the text is selectable anyway */
          }
        }}
        className="inline-flex max-w-full items-center gap-1.5 font-mono text-xs text-slate-500 hover:text-slate-800 group"
        data-testid={`copy-tx-id-${value}`}
        /* The visible text is truncated; the tooltip is how a reader gets the rest without
           committing to a clipboard write they may not want. */
        title={value}
        aria-label={t("actions.copyValue", { value })}
      >
        <span className={`truncate ${width}`}>{value}</span>
        {copied
          ? <Check className="w-3 h-3 text-emerald-600 shrink-0" />
          : <Copy className="w-3 h-3 text-slate-400 group-hover:text-slate-800 group-focus-visible:text-slate-800 shrink-0" />}
      </button>
      <span className="sr-only" aria-live="polite">{copied ? t("actions.copied") : ""}</span>
    </>
  );
}
